import { useEffect, useRef, useState } from "react";
import { LETTER_POINTS, scoreMove, validateShape, withPlacements } from "@gierki/shared";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";

/**
 * Scrabble na telefonie.
 *
 * Plansza 15×15 na ekranie szerokim na 390 px daje pola po ~24 px — za mało,
 * żeby celować w nie palcem przy odczycie, ale dość, żeby ją objąć wzrokiem.
 * Stąd sterowanie dwutaktowe: **najpierw wybierasz płytkę ze stojaka, potem
 * dotykasz pola**. Nie ma przeciągania, bo przeciąganie po 24-pikselowej
 * siatce w jadącym aucie jest nietrafialne.
 *
 * Płytki wyłożone w bieżącym ruchu są wyróżnione i da się je zdjąć dotknięciem,
 * dopóki nie klikniesz „Zagraj".
 */

export interface ScrabbleView {
  phase: "lobby" | "tura" | "sprzeciw" | "finished";
  hostId: string;
  players: string[];
  currentPlayerId: string | null;
  mojaTura: boolean;
  board: Array<Array<{ letter: string; blank: boolean } | null>>;
  premiums: string[][];
  boardSize: number;
  myRack: string[];
  rackCounts: Record<string, number>;
  bagCount: number;
  scores: Record<string, number>;
  history: Array<{ playerId: string; kind: string; words: string[]; points: number }>;
  pending: {
    playerId: string;
    words: Array<{ word: string; points: number; inDictionary: boolean }>;
    points: number;
    suspicious: string[];
    objections: string[];
    mogeSprzeciwic: boolean;
  } | null;
  canStart: boolean;
  canExchange: boolean;
  letterPoints: Record<string, number>;
}

/**
 * Premia za wyłożenie siedmiu płytek, użyta wyłącznie do podglądu punktów.
 * Serwer bierze ją z ustawień partii; gdyby host ją zmienił, podgląd byłby
 * o tyle nieprecyzyjny — ale tylko w tym jednym, rzadkim ruchu.
 */
const BINGO_BONUS = 50;

/** Wartości liter — do pokazania na płytce. Blank jest wart zero. */
const PUNKTY = LETTER_POINTS;

/*
 * Materiał planszy i płytek.
 *
 * Zdjęcia dają wyłącznie fakturę — litera, punkty i barwa premii nadal idą
 * z kodu i z tokenów. Wypalone w obrazku nie przeżyłyby zmiany motywu, a przy
 * trzydziestu pięciu znakach polskiego alfabetu i tak trzeba by ich trzydzieści
 * pięć.
 */
const TLO_POLA = "url(/scrabble/pole.png)";
const TLO_PLYTKI = "url(/scrabble/plytka.png)";

const KOLOR_PREMII: Record<string, string> = {
  S3: "bg-blad/50",
  S2: "bg-uwaga/40",
  P3: "bg-akcent/40",
  P2: "bg-akcent/20",
  "": "bg-panel",
};

const OPIS_PREMII: Record<string, string> = {
  S3: "3× słowo",
  S2: "2× słowo",
  P3: "3× litera",
  P2: "2× litera",
};

interface Wylozona {
  row: number;
  column: number;
  tile: string;
  as?: string;
  /** Indeks płytki na stojaku — żeby dało się ją zdjąć i oddać. */
  rackIndex: number;
}

export function Scrabble({
  view,
  nicki,
  mojeId,
  onStart,
  onZagraj,
  onPas,
  onWymiana,
  onSprzeciw,
}: {
  view: ScrabbleView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onZagraj: (placements: Array<{ row: number; column: number; tile: string; as?: string }>) => void;
  onPas: () => void;
  onWymiana: (tiles: string[]) => void;
  onSprzeciw: (on: boolean) => void;
}) {
  const [wybrana, setWybrana] = useState<number | null>(null);
  const [wylozone, setWylozone] = useState<Wylozona[]>([]);
  const [trybWymiany, setTrybWymiany] = useState(false);
  const [doWymiany, setDoWymiany] = useState<number[]>([]);

  if (view.phase === "lobby") {
    return (
      <>
        <Karta>
          <div className="text-sm text-tekst-slaby mb-2">Gracze</div>
          {view.players.map((id) => (
            <div key={id} className="flex justify-between py-1">
              <span>{nicki[id] ?? id}</span>
              {id === view.hostId ? <Odznaka>host</Odznaka> : null}
            </div>
          ))}
        </Karta>
        <Komunikat>
          Wybierz płytkę ze stojaka, potem dotknij pola na planszy. Słowa ze słownika SJP wchodzą od
          razu; przy słowie spoza słownika reszta może zgłosić sprzeciw.
        </Komunikat>
        {view.canStart ? (
          <Przycisk wariant="glowny" pelna onClick={onStart}>
            Zaczynamy
          </Przycisk>
        ) : (
          <Komunikat>Czekamy, aż host zacznie partię.</Komunikat>
        )}
      </>
    );
  }

  /** Płytki zajęte przez bieżący, niezatwierdzony ruch. */
  const zajete = new Set(wylozone.map((w) => w.rackIndex));

  const dotknijPole = (row: number, column: number) => {
    const juzTu = wylozone.find((w) => w.row === row && w.column === column);
    if (juzTu) {
      // Dotknięcie własnej, jeszcze niezatwierdzonej płytki zdejmuje ją z planszy.
      setWylozone(wylozone.filter((w) => w !== juzTu));
      return;
    }
    if (view.board[row]?.[column]) return;
    if (wybrana === null) return;

    const tile = view.myRack[wybrana] as string;
    if (tile === "?") {
      const litera = window.prompt("Jaką literą ma być blank?")?.trim().toLowerCase();
      if (!litera || !/^[a-ząćęłńóśźż]$/.test(litera)) return;
      setWylozone([...wylozone, { row, column, tile, as: litera, rackIndex: wybrana }]);
    } else {
      setWylozone([...wylozone, { row, column, tile, rackIndex: wybrana }]);
    }
    setWybrana(null);
  };

  const zagraj = () => {
    if (wylozone.length === 0) return;
    onZagraj(
      wylozone.map(({ row, column, tile, as }) => (as ? { row, column, tile, as } : { row, column, tile })),
    );
    setWylozone([]);
    setWybrana(null);
  };

  const wymien = () => {
    onWymiana(doWymiany.map((i) => view.myRack[i] as string));
    setDoWymiany([]);
    setTrybWymiany(false);
  };

  /**
   * Podgląd punktów za bieżące wyłożenie.
   *
   * Liczony tą samą funkcją, której użyje serwer — dlatego punktacja Scrabble
   * mieszka w `shared`. Druga implementacja po stronie klienta rozjechałaby się
   * przy pierwszej zmianie reguł, a objawiłoby się to jako „przycisk pokazał
   * 24, dostałem 18".
   *
   * Układ niepoprawny (dziura, brak styku, pierwsze słowo obok środka) rzuca
   * wyjątkiem — wtedy nie pokazujemy liczby, tylko sam powód pod przyciskiem.
   */
  const podglad = (():
    | { punkty: number; slowa: string[]; blad: null }
    | { punkty: null; slowa: []; blad: string } => {
    if (wylozone.length === 0) return { punkty: null, slowa: [], blad: "" };
    try {
      const placements = wylozone.map(({ row, column, tile, as }) =>
        as ? { row, column, tile, as } : { row, column, tile },
      );
      const kierunek = validateShape(view.board, placements);
      const plansza = withPlacements(view.board, placements);
      // `() => true` — klient liczy tylko punkty, a te nie zależą od słownika.
      const wynik = scoreMove(plansza, placements, kierunek, BINGO_BONUS, () => true);
      return { punkty: wynik.points, slowa: wynik.words.map((w) => w.word), blad: null };
    } catch (error) {
      return { punkty: null, slowa: [], blad: error instanceof Error ? error.message : "" };
    }
  })();

  const koniec = view.phase === "finished";
  const kolejnosc = [...view.players].sort((a, b) => (view.scores[b] ?? 0) - (view.scores[a] ?? 0));

  return (
    <>
      {koniec ? (
        <Karta>
          <div className="text-sm text-tekst-slaby mb-3">Wynik końcowy</div>
          {kolejnosc.map((id, miejsce) => (
            <div key={id} className="flex items-center gap-3 py-1">
              <span className="w-6 text-tekst-slaby tabular">{miejsce + 1}.</span>
              <span className="flex-1 truncate">
                {nicki[id] ?? id}
                {miejsce === 0 ? " 🏆" : ""}
              </span>
              <span className="text-xl font-semibold tabular">{view.scores[id] ?? 0}</span>
            </div>
          ))}
        </Karta>
      ) : (
        <Wyniki view={view} nicki={nicki} />
      )}

      {view.phase === "sprzeciw" && view.pending ? (
        <Sprzeciw view={view} nicki={nicki} mojeId={mojeId} onSprzeciw={onSprzeciw} />
      ) : null}

      <Plansza view={view} wylozone={wylozone} onPole={dotknijPole} aktywna={view.mojaTura} />

      {koniec ? null : (
        <>
          {/*
            Stojak jest **częścią przyklejonego paska**, a nie treści przewijanej.
            Wcześniej przewijał się razem z planszą i chował pod paskiem —
            a to jedyny element, który musi być widoczny w tej samej chwili co
            plansza, bo bez niego nie da się zaplanować ruchu.

            `-mx-4 px-4` rozciąga tło na pełną szerokość, górna krawędź oddziela
            pasek od planszy.
          */}
          <div className="sticky bottom-0 -mx-4 px-4 pt-1 bezpieczny-dol-waski bg-tlo border-t border-obrys space-y-1">
            <Stojak
              rack={view.myRack}
              wybrana={wybrana}
              zajete={zajete}
              trybWymiany={trybWymiany}
              doWymiany={doWymiany}
              onWybierz={(index) => {
                if (trybWymiany) {
                  setDoWymiany(
                    doWymiany.includes(index)
                      ? doWymiany.filter((i) => i !== index)
                      : [...doWymiany, index],
                  );
                } else if (view.mojaTura) {
                  setWybrana(wybrana === index ? null : index);
                }
              }}
            />
            {view.mojaTura ? (
              trybWymiany ? (
                <div className="flex gap-2">
                  <Przycisk
                    wariant="glowny"
                    onClick={wymien}
                    disabled={doWymiany.length === 0}
                    className="flex-1"
                  >
                    Wymień ({doWymiany.length})
                  </Przycisk>
                  <Przycisk
                    onClick={() => {
                      setTrybWymiany(false);
                      setDoWymiany([]);
                    }}
                  >
                    Anuluj
                  </Przycisk>
                </div>
              ) : (
                <>
                  {/* Powód odrzucenia pokazany od razu, a nie dopiero po
                      naciśnięciu „Zagraj" — inaczej trzeba zgadywać, dlaczego
                      układ jest nielegalny. */}
                  {podglad.blad ? (
                    <div className="text-center text-sm text-uwaga pb-1">{podglad.blad}</div>
                  ) : podglad.slowa.length > 0 ? (
                    <div className="text-center text-sm text-tekst-slaby pb-1">
                      {podglad.slowa.join(" · ")}
                    </div>
                  ) : null}

                  <Przycisk
                    wariant="glowny"
                    pelna
                    onClick={zagraj}
                    disabled={wylozone.length === 0 || podglad.punkty === null}
                    className="min-h-16 text-xl"
                  >
                    {wylozone.length === 0
                      ? "Wyłóż płytki"
                      : podglad.punkty === null
                        ? "Zagraj"
                        : `Zagraj za ${podglad.punkty} pkt`}
                  </Przycisk>
                  <div className="flex gap-2">
                    <Przycisk
                      onClick={() => setWylozone([])}
                      disabled={wylozone.length === 0}
                      className="flex-1"
                    >
                      Cofnij
                    </Przycisk>
                    <Przycisk
                      onClick={() => setTrybWymiany(true)}
                      disabled={!view.canExchange}
                      className="flex-1"
                    >
                      Wymiana
                    </Przycisk>
                    <Przycisk onClick={onPas} className="flex-1">
                      Pas
                    </Przycisk>
                  </div>
                </>
              )
            ) : (
              /* Większy napis, mniejszy odstęp: gdy nie jest Twoja tura, to
                 jedyna informacja w tym pasku — ma być czytelna z odległości
                 wyciągniętej ręki, a nie zajmować miejsca planszy. */
              <div className="text-center text-tekst-slaby text-2xl py-1">
                Tura: {nicki[view.currentPlayerId ?? ""] ?? "?"}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

/** Odległość między dwoma palcami. */
function odleglosc(dotyki: React.TouchList): number {
  const a = dotyki[0];
  const b = dotyki[1];
  if (!a || !b) return 0;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

const MAX_SKALA = 3.5;

function Plansza({
  view,
  wylozone,
  onPole,
  aktywna,
}: {
  view: ScrabbleView;
  wylozone: Wylozona[];
  onPole: (row: number, column: number) => void;
  aktywna: boolean;
}) {
  const swieze = new Map(wylozone.map((w) => [`${w.row}:${w.column}`, w]));

  /*
   * Zoom szczypaniem.
   *
   * Pole ma ~24 px, więc przy gęstej planszy odczytanie liter bywa niemożliwe,
   * a trafienie palcem w konkretne pole tym bardziej. Natywny zoom przeglądarki
   * jest wyłączony (`user-scalable=no` w viewport), bo przypadkowe przybliżenie
   * całego interfejsu w trakcie rundy było gorsze niż jego brak — więc zoom
   * obsługujemy sami i tylko dla planszy.
   *
   * Przy skali 1 `touch-action: pan-y` zostawia przewijanie strony jednym
   * palcem; dopiero po przybliżeniu przejmujemy gesty, żeby dało się przesuwać
   * planszę.
   */
  const [skala, setSkala] = useState(1);
  const [przesuniecie, setPrzesuniecie] = useState({ x: 0, y: 0 });
  const gest = useRef<{
    startOdleglosc: number;
    startSkala: number;
    startPunkt: { x: number; y: number };
    startPrzesuniecie: { x: number; y: number };
    przesuwanie: boolean;
  } | null>(null);

  const onTouchStart = (event: React.TouchEvent) => {
    if (event.touches.length === 2) {
      gest.current = {
        startOdleglosc: odleglosc(event.touches),
        startSkala: skala,
        startPunkt: { x: 0, y: 0 },
        startPrzesuniecie: przesuniecie,
        przesuwanie: false,
      };
    } else if (event.touches.length === 1 && skala > 1) {
      const dotyk = event.touches[0]!;
      gest.current = {
        startOdleglosc: 0,
        startSkala: skala,
        startPunkt: { x: dotyk.clientX, y: dotyk.clientY },
        startPrzesuniecie: przesuniecie,
        przesuwanie: true,
      };
    }
  };

  const onTouchMove = (event: React.TouchEvent) => {
    const stan = gest.current;
    if (!stan) return;

    if (event.touches.length === 2 && stan.startOdleglosc > 0) {
      const nowa = Math.min(
        MAX_SKALA,
        Math.max(1, (stan.startSkala * odleglosc(event.touches)) / stan.startOdleglosc),
      );
      setSkala(nowa);
      if (nowa === 1) setPrzesuniecie({ x: 0, y: 0 });
    } else if (stan.przesuwanie && event.touches.length === 1) {
      const dotyk = event.touches[0]!;
      // Granice przesuwania rosną ze skalą — przy 1× nie ma czego przesuwać.
      const limit = (skala - 1) * 50;
      setPrzesuniecie({
        x: Math.max(
          -limit,
          Math.min(limit, stan.startPrzesuniecie.x + (dotyk.clientX - stan.startPunkt.x) / skala),
        ),
        y: Math.max(
          -limit,
          Math.min(limit, stan.startPrzesuniecie.y + (dotyk.clientY - stan.startPunkt.y) / skala),
        ),
      });
    }
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    if (event.touches.length === 0) gest.current = null;
  };

  return (
    /*
     * Plansza ograniczona do kwadratu mieszczącego się w wysokości okna.
     *
     * Bez tego na szerokim ekranie rozciągała się na całą szerokość, spychając
     * stojak i przyciski poza widok — a przy 15 rzędach oznaczało to, że dolne
     * pola chowały się pod przyklejonym paskiem sterowania.
     */
    <div className="mx-auto w-full max-w-[62vh] relative">
      {skala > 1 ? (
        <button
          onClick={() => {
            setSkala(1);
            setPrzesuniecie({ x: 0, y: 0 });
          }}
          className="absolute -top-1 right-0 z-10 text-xs bg-panel-jasny border border-obrys rounded-lg px-2 py-1 text-tekst-slaby"
        >
          {skala.toFixed(1)}× · wróć
        </button>
      ) : null}

      <div
        className="overflow-hidden rounded-lg"
        style={{ touchAction: skala > 1 ? "none" : "pan-y" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
      <div
        className="grid gap-[1px] bg-obrys p-[1px] rounded-lg select-none origin-center"
        style={{
          gridTemplateColumns: `repeat(${view.boardSize}, minmax(0, 1fr))`,
          transform: `scale(${skala}) translate(${przesuniecie.x}%, ${przesuniecie.y}%)`,
          transition: gest.current ? "none" : "transform 150ms ease-out",
        }}
      >
        {view.board.flatMap((wiersz, row) =>
          wiersz.map((cell, column) => {
            const swieza = swieze.get(`${row}:${column}`);
            const litera = swieza ? (swieza.as ?? swieza.tile) : cell?.letter;
            const blank = swieza ? swieza.tile === "?" : cell?.blank;
            const premia = view.premiums[row]?.[column] ?? "";

            return (
              <button
                key={`${row}:${column}`}
                onClick={() => onPole(row, column)}
                disabled={!aktywna && !swieza}
                aria-label={
                  litera
                    ? `${row + 1},${column + 1}: ${litera}`
                    : `${row + 1},${column + 1}${OPIS_PREMII[premia] ? `, ${OPIS_PREMII[premia]}` : ""}`
                }
                className={[
                  "aspect-square relative flex items-center justify-center text-[13px] leading-none",
                  litera ? (swieza ? "font-bold" : "font-semibold") : (KOLOR_PREMII[premia] ?? "bg-panel"),
                ].join(" ")}
                /*
                 * Płytka to zdjęcie, pole planszy też — ale pod płytką świeżo
                 * wyłożoną zostaje akcent, żeby było widać, co dołożono w tym
                 * ruchu. Kolor pod obrazkiem, więc `multiply` niżej go przepuszcza.
                 */
                style={
                  litera
                    ? {
                        backgroundImage: TLO_PLYTKI,
                        backgroundSize: "100% 100%",
                        backgroundColor: swieza ? "var(--color-akcent)" : undefined,
                        backgroundBlendMode: swieza ? "multiply" : undefined,
                        // Korpus płytki to zawsze to samo jasne drewno, więc litera
                        // nie może chodzić za motywem — inaczej w ciemnym znika.
                        color: "var(--plytka-tekst)",
                      }
                    : {
                        backgroundImage: TLO_POLA,
                        backgroundSize: "100% 100%",
                        backgroundBlendMode: "multiply",
                      }
                }
              >
                {litera ? (
                  <>
                    <span className="uppercase">{litera}</span>
                    {!blank ? (
                      <span
                        className="absolute text-[8px] opacity-70"
                        style={{ bottom: "2px", right: "2px" }}
                      >
                        {PUNKTY[litera] ?? 0}
                      </span>
                    ) : null}
                  </>
                ) : premia ? (
                  /* Większe i półprzezroczyste: przy polu ~24 px napis 6 px był
                     nieczytelny, a pełny kontrast konkurowałby z literami.
                     Przezroczystość utrzymuje premie w tle, gdzie ich miejsce. */
                  <span className="text-[10px] font-semibold text-tekst/45">{premia}</span>
                ) : null}
              </button>
            );
          }),
        )}
        </div>
      </div>
    </div>
  );
}

/**
 * Stojak z przestawianiem płytek.
 *
 * Kolejność jest **wyłącznie wizualna**: `rackIndex` w wyłożonych płytkach,
 * `doWymiany` i `zajete` wskazują pozycje w tablicy od serwera, więc stojak
 * trzyma osobną permutację i tłumaczy pozycję na ekranie na indeks serwera.
 * Przestawianie na serwerze oznaczałoby akcję w protokole i zapis w stanie
 * partii — po to, żeby ułożyć sobie litery pod ręką.
 *
 * Przeciągnięcie jest odróżnione od dotknięcia progiem ośmiu pikseli. Bez
 * niego telefon w trzęsącym się aucie wybierałby płytkę przy każdej próbie
 * przesunięcia i odwrotnie.
 */
function Stojak({
  rack,
  wybrana,
  zajete,
  trybWymiany,
  doWymiany,
  onWybierz,
}: {
  rack: string[];
  wybrana: number | null;
  zajete: Set<number>;
  trybWymiany: boolean;
  doWymiany: number[];
  onWybierz: (index: number) => void;
}) {
  const [kolejnosc, setKolejnosc] = useState<number[]>(() => rack.map((_, index) => index));
  const listaRef = useRef<HTMLDivElement>(null);
  const przeciagane = useRef<{ pozycja: number; startX: number; ruszone: boolean } | null>(null);
  const pomijajKlik = useRef(false);
  const [ciagnieta, setCiagnieta] = useState<{ pozycja: number; dx: number } | null>(null);

  /*
   * Po zagraniu i dobraniu przychodzi inny stojak — układ trzeba wtedy
   * zacząć od nowa, bo pozycje nie opisują już tych samych liter. Sam podpis
   * (litery po kolei) nie zmienia się przy przestawianiu, więc własna zmiana
   * kolejności nie kasuje się nawzajem.
   */
  const podpis = rack.join("");
  useEffect(() => {
    setKolejnosc(rack.map((_, index) => index));
  }, [podpis, rack.length]);

  const start = (pozycja: number) => (event: React.PointerEvent<HTMLButtonElement>) => {
    przeciagane.current = { pozycja, startX: event.clientX, ruszone: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  /**
   * O ile miejsc przesunęła się płytka, licząc z przebytej odległości.
   *
   * Krok bierzemy z rzeczywistej szerokości sąsiadki plus odstęp — płytek bywa
   * siedem albo mniej, a stojak jest wyśrodkowany, więc stała szerokość
   * kłamałaby przy krótkim stojaku.
   */
  const oIleMiejsc = (dx: number, pozycja: number): number => {
    const dzieci = Array.from(listaRef.current?.children ?? []) as HTMLElement[];
    const wzorzec = dzieci.find((_e, i) => i !== pozycja) ?? dzieci[0];
    const krok = (wzorzec?.getBoundingClientRect().width ?? 44) + 4;
    return Math.round(dx / krok);
  };

  const ruch = (event: React.PointerEvent<HTMLButtonElement>) => {
    const stan = przeciagane.current;
    if (!stan) return;
    const dx = event.clientX - stan.startX;
    if (!stan.ruszone) {
      if (Math.abs(dx) < 8) return;
      stan.ruszone = true;
    }
    // Podczas ciągnięcia rusza się wyłącznie chwycona płytka; reszta stoi.
    // Wcześniej układ przestawiał się na bieżąco i „skakał" pod palcem.
    setCiagnieta({ pozycja: stan.pozycja, dx });
  };

  const koniec = (event: React.PointerEvent<HTMLButtonElement>) => {
    const stan = przeciagane.current;
    pomijajKlik.current = stan?.ruszone ?? false;

    if (stan?.ruszone) {
      const przesuniecie = oIleMiejsc(event.clientX - stan.startX, stan.pozycja);
      const docelowa = Math.max(0, Math.min(kolejnosc.length - 1, stan.pozycja + przesuniecie));
      if (docelowa !== stan.pozycja) {
        setKolejnosc((poprzednia) => {
          const nowa = [...poprzednia];
          const [przeniesiona] = nowa.splice(stan.pozycja, 1);
          nowa.splice(docelowa, 0, przeniesiona as number);
          return nowa;
        });
      }
    }

    przeciagane.current = null;
    setCiagnieta(null);
  };

  return (
    <div ref={listaRef} className="flex gap-1 justify-center py-1">
      {kolejnosc.map((index, pozycja) => {
        const tile = rack[index] as string;
        const uzyta = zajete.has(index);
        const zaznaczona = trybWymiany ? doWymiany.includes(index) : wybrana === index;

        return (
          <button
            key={index}
            onPointerDown={start(pozycja)}
            onPointerMove={ruch}
            onPointerUp={koniec}
            onPointerCancel={koniec}
            onClick={() => {
              if (pomijajKlik.current) {
                pomijajKlik.current = false;
                return;
              }
              onWybierz(index);
            }}
            disabled={uzyta}
            className={[
              "relative w-11 h-12 rounded-lg font-bold text-lg flex items-center justify-center transition-colors",
              /* Skala i uniesienie ciągniętej płytki idą w `style` razem
                 z przesunięciem: klasy Tailwinda i własny `transform` nie
                 mogą współistnieć — inline nadpisuje całość. */
              ciagnieta?.pozycja === pozycja ? "z-10" : "transition-transform",
              uzyta ? "opacity-30" : zaznaczona ? "-translate-y-1" : "",
            ].join(" ")}
            /*
             * Stojak dostaje tę samą płytkę co plansza. Wybrana nie zmienia
             * tła na akcent — korpus jest zdjęciem, więc zaznaczenie niesie
             * obwódka i uniesienie płytki, tak jak przy zatrzymanej kości.
             */
            style={{
              backgroundImage: TLO_PLYTKI,
              backgroundSize: "100% 100%",
              // Ta sama płytka co na planszy, więc i ta sama, stała barwa napisu.
              color: "var(--plytka-tekst)",
              touchAction: "pan-y",
              // Ciągnięta płytka jedzie za palcem; bez tego nie widać, co się chwyciło.
              transform:
                ciagnieta?.pozycja === pozycja
                  ? `translate(${ciagnieta.dx}px, -6px) scale(1.1)`
                  : undefined,
            }}
          >
            {/*
              Zaznaczenie przesłoną, nie samą obwódką: płytka jest jasnym
              zdjęciem, na którym cienki pierścień ginie. Półprzezroczysty
              akcent widać z drugiego siedzenia, a litera pod nim zostaje
              czytelna.
            */}
            {zaznaczona && (
              <span
                className="absolute inset-0 rounded-lg"
                style={{ backgroundColor: "var(--color-akcent)", opacity: 0.5 }}
              />
            )}
            <span className="relative uppercase">{tile === "?" ? "★" : tile}</span>
            {tile !== "?" ? (
              /* Offsety w `style`, nie klasami: dowolne wartości Tailwinda
                 muszą trafić do wygenerowanego arkusza, a bez nich `absolute`
                 siada w pozycji statycznej — czyli na środku płytki. */
              <span
                className="absolute text-[9px] opacity-70"
                style={{ bottom: "3px", right: "5px", zIndex: 1 }}
              >
                {PUNKTY[tile] ?? 0}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Pasek punktów przypięty pod nagłówkiem.
 *
 * Kto ile ma i czyja jest tura to informacja potrzebna w każdej chwili gry,
 * a przy planszy wysokiej na cały ekran karta u góry natychmiast wyjeżdżała
 * poza widok. Stąd `sticky` i układ w jednym wierszu na gracza zamiast karty —
 * przypięty pasek zabiera miejsce planszy, więc musi być niski.
 *
 * `-mx-4 px-4` rozciąga tło na pełną szerokość, `-mt-4 pt-4` zakrywa górny
 * odstęp ekranu, żeby przewijana treść nie prześwitywała nad paskiem.
 */
function Wyniki({ view, nicki }: { view: ScrabbleView; nicki: Record<string, string> }) {
  const ostatni = view.history.at(-1);

  return (
    <div className="sticky top-0 z-20 -mx-4 -mt-4 px-4 pt-4 pb-2 bg-tlo border-b border-obrys">
      <div className="flex items-center gap-3 flex-wrap">
        {view.players.map((id) => (
          <div key={id} className="flex items-baseline gap-1.5">
            <span
              className={[
                "truncate max-w-24",
                id === view.currentPlayerId ? "text-akcent font-semibold" : "text-tekst-slaby",
              ].join(" ")}
            >
              {nicki[id] ?? id}
            </span>
            <span className="font-semibold tabular">{view.scores[id] ?? 0}</span>
            <span className="text-[10px] text-tekst-slaby">
              ({view.rackCounts[id] ?? 0})
            </span>
          </div>
        ))}
        <Odznaka>woreczek: {view.bagCount}</Odznaka>
      </div>

      {ostatni ? (
        <div className="text-xs text-tekst-slaby truncate mt-1">
          {nicki[ostatni.playerId] ?? "?"}:{" "}
          {ostatni.kind === "slowo"
            ? `${ostatni.words.join(", ")} — ${ostatni.points} pkt`
            : ostatni.kind === "cofniety"
              ? `${ostatni.words.join(", ")} — ruch cofnięty`
              : ostatni.kind}
        </div>
      ) : null}
    </div>
  );
}

function Sprzeciw({
  view,
  nicki,
  mojeId,
  onSprzeciw,
}: {
  view: ScrabbleView;
  nicki: Record<string, string>;
  mojeId: string;
  onSprzeciw: (on: boolean) => void;
}) {
  const pending = view.pending!;
  const mojSprzeciw = pending.objections.includes(mojeId);

  return (
    <Karta>
      <div className="text-sm text-tekst-slaby mb-1">
        {nicki[pending.playerId] ?? "?"} wyłożył słowo spoza słownika
      </div>
      <div className="text-2xl font-semibold my-2">{pending.suspicious.join(", ")}</div>
      <div className="text-sm text-tekst-slaby mb-3">
        za {pending.points} pkt · sprzeciwy: {pending.objections.length}
      </div>

      {pending.mogeSprzeciwic ? (
        <div className="flex gap-2">
          <Przycisk
            wariant={mojSprzeciw ? "blad" : "zwykly"}
            onClick={() => onSprzeciw(!mojSprzeciw)}
            className="flex-1"
          >
            {mojSprzeciw ? "Sprzeciw zgłoszony" : "Nie uznaję"}
          </Przycisk>
        </div>
      ) : (
        <Komunikat>To Twój ruch — decydują pozostali.</Komunikat>
      )}
    </Karta>
  );
}
